import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  TruckIcon,
  EyeIcon,
  TrashIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'
import { ambulanceBookingService } from '../../services/ambulance-booking.service'
import { formatShortDate, formatStatus, getStatusColor } from '../../utils/formatters'
import LoadingSpinner from '../../components/LoadingSpinner'

function AmbulanceBookingsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [bookings, setBookings] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [sortBy, setSortBy] = useState<'createdAt' | 'scheduledDateTime'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const limit = 10

  const fetchBookings = async () => {
    setIsLoading(true)
    try {
      const options = {
        page: currentPage,
        limit,
        status: statusFilter || undefined,
        sortBy,
        sortOrder,
      }
      const result = await ambulanceBookingService.getBookingsList(options)
      setBookings(result.bookings)
      setTotal(result.total)
      setTotalPages(result.totalPages)
    } catch (error) {
      console.error('Failed to fetch ambulance bookings:', error)
      // Show toast or error message
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [currentPage, statusFilter, sortBy, sortOrder])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Implement search functionality (currently searchQuery is not used in API)
    fetchBookings()
  }

  const handleSort = (column: 'createdAt' | 'scheduledDateTime') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
    setCurrentPage(1)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status === statusFilter ? '' : status)
    setCurrentPage(1)
  }

  const handleDeleteBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return
    }

    try {
      await ambulanceBookingService.deleteBooking(bookingId)
      // Refresh the bookings list
      fetchBookings()
      // Show success message (you could add a toast notification here)
    } catch (error) {
      console.error('Failed to delete booking:', error)
      // Show error message (you could add a toast notification here)
    }
  }

  const getStatusOptions = () => {
    const statuses = bookings.map(booking => booking.status)
    return [...new Set(statuses)]
  }

  const isPaymentPending = (booking: any) => {
    // First, check if payment is already completed - exclude these
    if (booking.paymentStatus === 'PAID' ||
        booking.paymentStatus === 'VERIFIED' ||
        booking.paymentStatus === 'COMPLETED') {
      return false;
    }

    // Check if payment is submitted and awaiting verification - exclude these too
    if (booking.paymentStatus === 'PENDING' &&
        (booking.workflow?.currentStep === 'PAYMENT_SUBMITTED' ||
         booking.workflow?.nextStep === 'AWAITING_PAYMENT_VERIFICATION' ||
         booking.workflow?.currentStep?.toLowerCase().includes('verification'))) {
      return false;
    }

    // Check if paymentStatus exists and indicates pending (but not submitted)
    if (booking.paymentStatus === 'PENDING') {
      // Check if this is initial payment pending (not submitted yet)
      const currentStep = booking.workflow?.currentStep || '';
      const nextStep = booking.workflow?.nextStep || '';

      // Only return true if we're still at the initial payment stage
      if (currentStep === 'Booking Created' &&
          (nextStep === 'Awaiting Payment' || nextStep.toLowerCase().includes('payment'))) {
        return true;
      }
    }

    // Check if workflow status indicates payment is pending (but not submitted)
    const currentStep = booking.workflow?.currentStep || '';
    const nextStep = booking.workflow?.nextStep || '';

    if (nextStep.toLowerCase().includes('payment') ||
        currentStep.toLowerCase().includes('awaiting payment') ||
        currentStep === 'Awaiting Payment') {
      // Make sure we're not in the verification stage
      if (!currentStep.toLowerCase().includes('verification') &&
          !nextStep.toLowerCase().includes('verification')) {
        return true;
      }
    }

    // Check if booking status itself indicates payment pending
    if (booking.status === 'PENDING_PAYMENT') {
      return true;
    }

    return false;
  }

  if (isLoading && bookings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Ambulance Bookings</h1>
            <p className="mt-1 text-sm text-neutral-600">
              Manage and track all ambulance transport requests
            </p>
          </div>
          <Link
            to="/bookings/ambulance/new"
            className="btn-primary flex items-center gap-2"
          >
            <PlusCircleIcon className="h-5 w-5" />
            New Booking
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                className="input pl-10"
                placeholder="Search by booking ID, provider name, or route..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <button
            type="submit"
            className="btn-outline"
          >
            Search
          </button>
        </form>

        {/* Status Filter */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-sm text-neutral-600 py-2">Filter by status:</span>
          {getStatusOptions().map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                statusFilter === status
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-neutral-300 text-neutral-600 hover:border-neutral-400'
              }`}
            >
              {formatStatus(status)}
            </button>
          ))}
          {statusFilter && (
            <button
              onClick={() => {
                setStatusFilter('')
                setCurrentPage(1)
              }}
              className="px-3 py-1 text-sm rounded-full border border-neutral-300 text-neutral-600 hover:border-neutral-400"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Bookings List */}
      <div className="card">
        <div className="card-body">
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <TruckIcon className="mx-auto h-12 w-12 text-neutral-400" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900">No ambulance bookings found</h3>
              <p className="mt-1 text-sm text-neutral-500">
                {searchQuery || statusFilter ? 'Try adjusting your search terms or filters.' : 'Get started by creating your first ambulance booking.'}
              </p>
              <div className="mt-6">
                {!searchQuery && !statusFilter && (
                  <Link
                    to="/bookings/ambulance/new"
                    className="btn-primary"
                  >
                    Create New Booking
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Booking ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Transfer Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Provider Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700"
                        onClick={() => handleSort('createdAt')}
                    >
                      Created
                      {sortBy === 'createdAt' && (
                        <span className="ml-1">
                          {sortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </th>
                    <th className="relative px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {booking.bookingId}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-900">
                        <div className="max-w-xs truncate" title={booking.route}>
                          {booking.route}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                        {booking.providerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                          {formatStatus(booking.status)}
                        </span>
                        {isPaymentPending(booking) && (
                          <div className="mt-1">
                            <span className="inline-flex px-1 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                              Payment Pending
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {formatShortDate(booking.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {isPaymentPending(booking) && (
                            <Link
                              to={`/bookings/${booking.id}/payment`}
                              className="text-green-600 hover:text-green-900"
                              title="Complete Payment"
                            >
                              <CreditCardIcon className="h-4 w-4" />
                            </Link>
                          )}
                          <Link
                            to={`/bookings/${booking.id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Booking"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="card-footer">
              <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-700">
                  Showing {((currentPage - 1) * limit) + 1} to{' '}
                  {Math.min(currentPage * limit, total)} of{' '}
                  {total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-neutral-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AmbulanceBookingsPage